import React, { useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import Alert from "@mui/material/Alert";
import { formatCurrencyUSD } from "utils/CommonUtils";
import OcrolusDataTable from "./OcrolusDataTable";
import { CircularProgress } from "@mui/material";
import Loader from "components/Loader/Basic";

interface AdvancesPositionsProps {
  advances: any[];
  setPositionCount?: (count: number) => void;
  loading: boolean;
}

const OcrolusAdvancesPositions: React.FC<AdvancesPositionsProps> = ({
  advances,
  setPositionCount,
  loading
}) => {
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [pendingRow, setPendingRow] = useState<any | null>(null);


  const filteredPositions = useMemo(() => {
    return (advances || [])
      .filter((a) => Number(a?.fundedAmount) > 0)
      .map((a, index) => ({
        ...a,
        _rowId: `${a?.funder ?? "UNKNOWN"}-${index}`,
      }));
  }, [advances]);

 
  useEffect(() => {
    const ids = filteredPositions.map((r) => r._rowId);
    setSelectedRows(ids);
  }, [filteredPositions]);

  useEffect(() => {
    setPositionCount?.(selectedRows.length);
  }, [selectedRows, setPositionCount]);

  const handleSelectRow = (rowId: string) => {
    setSelectedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredPositions.map((r) => r._rowId);
    const allSelected = allIds.every((id) => selectedRows.includes(id));
    setSelectedRows(allSelected ? [] : allIds);
  };

 
  const columns = [
    { Header: "FUNDER", accessor: "funder", width: 150, align: "left" },
    { Header: "TYPE", accessor: "type", width: 80, align: "left" },
    {
      Header: "FUNDED DATE",
      accessor: "fundedDate",
      input: "date",
      width: 110,
      align: "left",
    },
    {
      Header: "FUNDED AMOUNT",
      accessor: "fundedAmount",
      input: "amount",
      width: 140,
      align: "left",
      Cell: ({ row }: any) => formatCurrencyUSD(row?.fundedAmount),
    },
    {
      Header: "LAST PAYMENT",
      accessor: "lastPayment",
      input: "date",
      width: 120,
      align: "left",
    },
    {
      Header: "EST. TERM",
      accessor: "estTerm",
      input: "text",
      width: 100,
      align: "left",
    },
    {
      Header: "EST. BALANCE",
      accessor: "estBalance",
      input: "amount",
      width: 130,
      align: "left",
      Cell: ({ row }: any) => formatCurrencyUSD(row?.estBalance),
    },
  ];

  const renderTotal = (key: string) => {
    let total = 0;

    if (editingRow && selectedRows.includes(editingRow._rowId)) {
      total += Number(editingRow[key]) || 0;
    }

    filteredPositions
      .filter((r) => selectedRows.includes(r._rowId))
      .filter((r) => !editingRow || r._rowId !== editingRow._rowId)
      .forEach((r) => {
        total += Number(r[key]) || 0;
      });

    return key.toLowerCase().includes("amount") ||
      key.toLowerCase().includes("balance")
      ? formatCurrencyUSD(total)
      : total.toFixed(2);
  };

  return (
    <CollapsibleCard header="Advances / Positions">
      <MDBox pr={3}>
      { loading ? (
        <Loader/>
      ):
        <OcrolusDataTable
          columns={columns}
          data={filteredPositions}
          rowKey="_rowId"
          selectable
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          renderTotal={renderTotal}
          totalColumns={["fundedAmount"]}
        />
       }
      </MDBox>

      {showEditWarning && (
        <Alert severity="warning" onClose={() => setShowEditWarning(false)}>
          This advance is system-generated. Editing may cause inconsistencies.
        </Alert>
      )}

      {showDeleteAlert && pendingRow && (
        <Alert
          severity="info"
          action={
            <MDButton
              color="error"
              onClick={() => {
                setShowDeleteAlert(false);
                setPendingRow(null);
              }}
            >
              Delete
            </MDButton>
          }
        >
          Deleting this advance may affect debit calculations.
        </Alert>
      )}
    </CollapsibleCard>
  );
};

export default OcrolusAdvancesPositions;
